import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Avatar from './Avatar';
import { testimonialsAPI, Testimonial } from '../services/testimonials';
import './HomeTestimonials.css';

/**
 * Home-page testimonial strip. Pulls the hand-picked banner pool
 * (?banner=true → show_in_banner) so the team controls exactly which real
 * student voices lead the marketing page. Text testimonials only here — the
 * full feed (video/voice/all) lives at /testimonials.
 */

const HomeTestimonials: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<Testimonial[]>([]);

  useEffect(() => {
    let alive = true;
    testimonialsAPI
      .list({ banner: true, limit: 24 })
      .then((r) => {
        if (!alive) return;
        const text = r.testimonials.filter((t) => t.quote && t.quote.trim().length > 0);
        setItems(text.slice(0, 12));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (items.length === 0) return null;

  return (
    <section className="home-testimonials">
      <div className="home-testimonials-header">
        <span className="home-testimonials-eyebrow">In their words</span>
        <h2>Students aren't quiet about it</h2>
        <p>Unprompted messages from students across Ethiopia — in English, Amharic, and Afaan Oromo.</p>
      </div>

      <div className="home-testimonials-marquee">
        <div className="home-testimonials-track">
          {/* The second copy exists only to make the marquee loop seamlessly. It is
              marked as a clone so it can be hidden on mobile (where the track
              stacks vertically) and hidden from assistive tech, which was
              otherwise announcing every testimonial twice. */}
          {[...items, ...items].map((t, i) => (
            <figure
              className={`home-testimonial-card${i >= items.length ? ' is-clone' : ''}`}
              aria-hidden={i >= items.length || undefined}
              key={`${t.id}-${i}`}
            >
              <blockquote>{t.quote}</blockquote>
              <figcaption>
                <Avatar url={t.author_avatar_url} name={t.author_name} size={36} />
                <span className="home-testimonial-author">
                  <span className="home-testimonial-name">{t.author_name}</span>
                  {t.author_detail && (
                    <span className="home-testimonial-detail">{t.author_detail}</span>
                  )}
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>

      <div className="home-testimonials-cta">
        <button className="home-testimonials-btn" onClick={() => navigate('/testimonials')}>
          Read more student stories →
        </button>
      </div>
    </section>
  );
};

export default HomeTestimonials;
